<script setup>
import { computed, ref, watch } from 'vue';
import InputPublishedForm from '../../forms/inputPublishedForm.vue';
import ButtonUnifiedConfig from './_button-unified-config.vue';
import { componentCatalog, isFieldVisible as isCatalogFieldVisible } from './component-catalog';

const emit = defineEmits(['insert', 'update', 'remove', 'attachment-configured']);

const configModal = ref(null);
const configValues = ref({});
const editingIndex = ref(null);
const editingBaseConfig = ref({});
const modalMode = ref(null);
const attachmentIndex = ref(null);
const loadingBookableUsers = ref(false);
const bookableUsers = ref([]);
const hasLoadedBookableUsers = ref(false);
const loadingInvoiceLinks = ref(false);
const invoiceLinks = ref([]);
const hasLoadedInvoiceLinks = ref(false);
const isEditingComponent = computed(() => modalMode.value === 'placed' && editingIndex.value !== null);
const isEditingAttachment = computed(() => modalMode.value === 'attachment' && attachmentIndex.value !== null);
let imageMetadataRequestToken = 0;

const buttonPaddingPresets = {
    small: {
        top: 'tiny',
        right: 'small',
        bottom: 'tiny',
        left: 'small',
    },
    medium: {
        top: 'small',
        right: 'medium',
        bottom: 'small',
        left: 'medium',
    },
    large: {
        top: 'medium',
        right: 'large',
        bottom: 'medium',
        left: 'large',
    },
};

const isPlainObject = (value) => Object.prototype.toString.call(value) === '[object Object]';

const cloneConfig = (value) => JSON.parse(JSON.stringify(value ?? {}));

const mergeConfig = (base, override) => {
    const result = cloneConfig(base);

    Object.entries(override ?? {}).forEach(([key, value]) => {
        if (isPlainObject(value) && isPlainObject(result[key])) {
            result[key] = mergeConfig(result[key], value);
            return;
        }

        result[key] = value;
    });

    return result;
};

const buildDefaultConfig = (component) => {
    const fieldDefaults = {};
    component.configFields.forEach((field) => {
        // A key can be listed once per showWhen variant; the first listing owns the default.
        if (field.key in fieldDefaults) {
            return;
        }

        fieldDefaults[field.key] = field.defaultKey ? _mft(field.defaultKey) : (field.default ?? null);
    });

    return mergeConfig(fieldDefaults, component.defaultConfig ?? {});
};

const getButtonPaddingSize = (config = {}) => {
    const horizontalPadding = config.padding?.right ?? config.padding?.left;

    if (horizontalPadding === 'small' || horizontalPadding === 'tiny' || horizontalPadding === 'none') {
        return 'small';
    }

    if (horizontalPadding === 'large' || horizontalPadding === 'huge') {
        return 'large';
    }

    return 'medium';
};

const buildConfigFormValues = (component, config = {}) => {
    const values = mergeConfig(buildDefaultConfig(component), config);

    if (component.type === 'button-unified') {
        values.padding_size = config.padding_size ?? getButtonPaddingSize(config);
    }

    return values;
};

const normalizeConfigForSave = (component, config = {}) => {
    const values = cloneConfig(config);

    if (component.type === 'button-unified') {
        const paddingSize = values.padding_size ?? getButtonPaddingSize(values);
        values.padding = cloneConfig(buttonPaddingPresets[paddingSize] ?? buttonPaddingPresets.medium);
        delete values.padding_size;
    }

    if (component.type === 'image-unified') {
        values.source_type = ['mediaLibrary', 'pexels', 'url'].includes(values.source_type)
            ? values.source_type
            : 'mediaLibrary';
        values.media_width_aspect = values.media_width_aspect || 1;
        values.media_height_aspect = values.media_height_aspect || 1;
        values.max_width_unit = values.max_width_unit || '%';

        if (values.source_type === 'mediaLibrary') {
            values.media_url = null;
        } else {
            values.media_bank_entry_id = null;
        }
    }

    return values;
};

const components = componentCatalog;

const isFieldVisible = (field) => isCatalogFieldVisible(field, configValues.value);

const canConfirm = computed(() => configModal.value?.configFields.every((field) => {
    if (!field.required || !isFieldVisible(field)) {
        return true;
    }

    const value = configValues.value[field.key];

    if (field.type === 'page-search') {
        return Boolean(value?.slug);
    }

    return value !== null && value !== undefined && value !== '';
}) ?? false);

// Components whose settings mirror a page builder block editor get a dedicated layout
// instead of the generic field list. Their catalog configFields still supply defaults
// and required-field validation.
const customConfigEditors = {
    'button-unified': ButtonUnifiedConfig,
};

const applyConfigPatch = (patch) => {
    Object.assign(configValues.value, patch);
};

const handleTagSelected = (tags) => {
    if (tags && tags.length > 0) {
        configValues.value['leadTag'] = tags[0].uuid;
    } else {
        configValues.value['leadTag'] = '';
    }
};

const handlePexelsImageSelected = (field, image) => {
    configValues.value[field.key] = image.src.large2x ? image.src.large2x : image.src.original;
    configValues.value.media_width = image.width ?? null;
    configValues.value.media_height = image.height ?? null;
    configValues.value.media_width_aspect = 1;
    configValues.value.media_height_aspect = image.width
        ? Math.round(((image.height ?? image.width) / image.width) * 100) / 100
        : 1;
};

const resetImageMetadata = () => {
    configValues.value.media_width = null;
    configValues.value.media_height = null;
    configValues.value.media_width_aspect = 1;
    configValues.value.media_height_aspect = 1;
};

const applyImageMetadataFromUrl = (url, token) => {
    if (!url) {
        resetImageMetadata();
        return;
    }

    const image = new Image();

    image.onload = () => {
        if (token !== imageMetadataRequestToken) {
            return;
        }

        configValues.value.media_width = image.naturalWidth;
        configValues.value.media_height = image.naturalHeight;
        configValues.value.media_width_aspect = 1;
        configValues.value.media_height_aspect = image.naturalWidth
            ? Math.round((image.naturalHeight / image.naturalWidth) * 100) / 100
            : 1;
    };

    image.onerror = () => {
        if (token === imageMetadataRequestToken) {
            resetImageMetadata();
        }
    };

    image.src = url;
};

const syncImageUnifiedMetadata = async () => {
    if (configModal.value?.type !== 'image-unified') {
        return;
    }

    const token = ++imageMetadataRequestToken;
    const sourceType = configValues.value.source_type;

    if (sourceType === 'mediaLibrary') {
        const entryId = configValues.value.media_bank_entry_id;

        if (!entryId) {
            resetImageMetadata();
            return;
        }

        try {
            const { data } = await axios.get(route('mediaBank.entries.show', entryId));

            if (token !== imageMetadataRequestToken) {
                return;
            }

            applyImageMetadataFromUrl(data?.url, token);
        } catch (error) {
            if (token === imageMetadataRequestToken) {
                resetImageMetadata();
            }
        }

        return;
    }

    applyImageMetadataFromUrl(configValues.value.media_url, token);
};

watch(() => [
    configModal.value?.type,
    configValues.value.source_type,
    configValues.value.media_bank_entry_id,
    configValues.value.media_url,
], () => {
    syncImageUnifiedMetadata();
});

const getPreSelectedTag = () => {
    const val = configValues.value['leadTag'];
    return val ? [val] : [];
};

const formatInvoiceLinkOption = (link) => {
    const currency = typeof link.currency === 'string' ? link.currency.toUpperCase() : '';
    const price = link.price ? ` - ${link.price}${currency ? ` ${currency}` : ''}` : '';

    return `${link.title}${price}`;
};

const applySingleBookableUserDefault = () => {
    if (configModal.value?.type !== 'cronofy') {
        return;
    }

    if (!configValues.value.bookableUserUuid && bookableUsers.value.length === 1) {
        configValues.value.bookableUserUuid = bookableUsers.value[0].uuid;
    }
};

const applySingleInvoiceLinkDefault = () => {
    if (configModal.value?.type !== 'invoice-link') {
        return;
    }

    if (!configValues.value.paymentLinkUuid && invoiceLinks.value.length === 1) {
        configValues.value.paymentLinkUuid = invoiceLinks.value[0].uuid;
    }
};

const loadBookableUsers = async () => {
    if (hasLoadedBookableUsers.value || loadingBookableUsers.value) {
        applySingleBookableUserDefault();
        return;
    }

    loadingBookableUsers.value = true;

    try {
        const res = await axios.get(route('block-editor-block.booking.bookingOptions'));
        bookableUsers.value = res.data.bookable_users ?? [];
        hasLoadedBookableUsers.value = true;
        applySingleBookableUserDefault();
    } catch (error) {
        console.warn('Unable to load bookable users for meeting booking component', error);
    } finally {
        loadingBookableUsers.value = false;
    }
};

const loadInvoiceLinks = async () => {
    if (hasLoadedInvoiceLinks.value || loadingInvoiceLinks.value) {
        applySingleInvoiceLinkDefault();
        return;
    }

    loadingInvoiceLinks.value = true;

    try {
        const res = await axios.get(route('api.invoice-links.index', { type: 'multi-use', active: 1 }));
        invoiceLinks.value = res.data.links ?? [];
        hasLoadedInvoiceLinks.value = true;
        applySingleInvoiceLinkDefault();
    } catch (error) {
        console.warn('Unable to load invoice links for invoice link component', error);
    } finally {
        loadingInvoiceLinks.value = false;
    }
};

const ensureComponentPrerequisites = (component) => {
    if (component.type === 'cronofy') {
        loadBookableUsers();
    }

    if (component.type === 'invoice-link') {
        loadInvoiceLinks();
    }
};

const editComponent = (componentType, config, index) => {
    const component = components.find(c => c.type === componentType);
    if (!component) return;

    const currentConfig = typeof config === 'object' && config !== null ? config : {};

    editingBaseConfig.value = cloneConfig(currentConfig);
    configValues.value = buildConfigFormValues(component, currentConfig);
    editingIndex.value = index;
    modalMode.value = 'placed';
    configModal.value = component;
    ensureComponentPrerequisites(component);
};

const configureAttachment = (componentType, config = {}, index = null) => {
    const component = components.find(c => c.type === componentType);
    if (!component) return;

    const currentConfig = typeof config === 'object' && config !== null ? config : {};
    const normalizedConfig = normalizeConfigForSave(component, buildConfigFormValues(component, currentConfig));

    if (component.configFields.length === 0) {
        emit('attachment-configured', { type: component.type, config: normalizedConfig, index });
        return;
    }

    editingBaseConfig.value = cloneConfig(currentConfig);
    configValues.value = buildConfigFormValues(component, currentConfig);
    attachmentIndex.value = index;
    modalMode.value = 'attachment';
    configModal.value = component;
    ensureComponentPrerequisites(component);
};

const confirmInsert = () => {
    const component = configModal.value;

    if (modalMode.value === 'attachment') {
        const mergedConfig = mergeConfig(editingBaseConfig.value, configValues.value);
        const normalizedConfig = normalizeConfigForSave(component, mergedConfig);
        emit('attachment-configured', {
            type: component.type,
            config: normalizedConfig,
            index: attachmentIndex.value,
        });
        closeConfigModal();
        return;
    }

    if (editingIndex.value !== null) {
        const mergedConfig = mergeConfig(editingBaseConfig.value, configValues.value);
        const normalizedConfig = normalizeConfigForSave(component, mergedConfig);
        const configStr = JSON.stringify(normalizedConfig).replace(/'/g, '&#39;');
        emit('update', {
            type: component.type,
            config: configStr,
            index: editingIndex.value,
        });
        closeConfigModal();
        return;
    }

    const normalizedConfig = normalizeConfigForSave(component, configValues.value);
    const configStr = JSON.stringify(normalizedConfig).replace(/'/g, '&#39;');
    const html = `<mf-component type="${component.type}" config='${configStr}'></mf-component>`;
    emit('insert', html);
    configModal.value = null;
};

const closeConfigModal = () => {
    configModal.value = null;
    editingIndex.value = null;
    editingBaseConfig.value = {};
    modalMode.value = null;
    attachmentIndex.value = null;
};

const deleteComponent = () => {
    if (editingIndex.value === null) {
        return;
    }

    emit('remove', { index: editingIndex.value });
    closeConfigModal();
};

const isWideModal = (component) => {
    return component?.configFields?.some((field) => field.type === 'media-bank-gallery');
};

const getOptionLabel = (field, option) => {
    if (field.key === 'source_type') {
        if (option === 'mediaLibrary') {
            return _mft('page:aiPageBuilder.components.image.source.mediaLibrary');
        }

        if (option === 'pexels') {
            return 'Pexels';
        }

        if (option === 'url') {
            return 'URL';
        }
    }

    if (field.key === 'rounding') {
        return _mft(`pagebuilderBlock:general.input.rounding.${option}`);
    }

    if (field.key === 'alignment') {
        return _mft(`pagebuilderBlock:general.input.alignment.${option}`);
    }

    if (field.key === 'vatDisplayMode') {
        return _mft(`pagebuilderBlock:invoiceLink.showPrice${option === 'inclVat' ? 'InclVat' : 'ExclVat'}`);
    }

    if (option === 'siteAppearance') {
        return _mft('shared:default');
    }

    return option;
};

defineExpose({ editComponent, configureAttachment });
</script>

<template>
    <!-- Shared configuration slideout for placed components and chat attachments. -->
    <div>
        <slideout-with-slots
            v-if="configModal"
            :max-width-class="isWideModal(configModal) ? 'max-w-2xl' : 'max-w-lg'"
            @close="closeConfigModal"
        >
            <template #title>
                <h1 class="text-xl font-semibold">
                    {{ _mft(configModal.nameKey) }} - {{ _mft('page:aiPageBuilder.components.settings') }}
                </h1>
                <p v-if="isEditingComponent" class="mt-1 text-xs text-wlPrimaryContrast/80">
                    {{ _mft('page:aiPageBuilder.components.editingExisting') }}
                </p>
            </template>

            <template #body>
                <component
                    v-if="customConfigEditors[configModal.type]"
                    :is="customConfigEditors[configModal.type]"
                    :model-value="configValues"
                    @patch="applyConfigPatch"
                />
                <template v-else>
                    <template v-for="field in configModal.configFields" :key="`${field.key}:${field.type}:${field.showWhen ?? field.showWhenNot ?? ''}`">
                        <div v-if="isFieldVisible(field)" :class="field.type === 'boolean' ? 'mb-2' : 'mb-4'">
                            <template v-if="field.type === 'boolean'">
                                <label class="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        v-model="configValues[field.key]"
                                        class="rounded border-gray-300"
                                    >
                                    <span class="text-sm text-gray-700">{{ _mft(field.labelKey) }}</span>
                                </label>
                            </template>
                            <template v-else-if="field.type === 'media-bank-gallery'">
                                <label class="block text-sm font-medium text-gray-700 mb-1">{{ _mft(field.labelKey) }}</label>
                                <input-media-bank-gallery
                                    :type="field.mediaType"
                                    :required="Boolean(field.required)"
                                    :selected-media-bank-entry-id="configValues[field.key]"
                                    v-model:content="configValues[field.key]"
                                ></input-media-bank-gallery>
                            </template>
                            <template v-else-if="field.type === 'pexels-search'">
                                <label class="block text-sm font-medium text-gray-700 mb-1">{{ _mft(field.labelKey) }}</label>
                                <input-pexels-search
                                    @image-selected="(image) => handlePexelsImageSelected(field, image)"
                                />
                                <p v-if="configValues[field.key]" class="mt-2 break-all text-xs text-gray-500">
                                    {{ configValues[field.key] }}
                                </p>
                            </template>
                            <template v-else-if="field.type === 'number'">
                                <label class="block text-sm font-medium text-gray-700 mb-1">{{ _mft(field.labelKey) }}</label>
                                <input
                                    type="number"
                                    v-model.number="configValues[field.key]"
                                    class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                                >
                            </template>
                            <template v-else-if="field.type === 'select'">
                                <label class="block text-sm font-medium text-gray-700 mb-1">{{ _mft(field.labelKey) }}</label>
                                <select
                                    v-model="configValues[field.key]"
                                    class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                                >
                                    <option v-for="opt in field.options" :key="opt" :value="opt">{{ getOptionLabel(field, opt) }}</option>
                                </select>
                            </template>
                            <template v-else-if="field.type === 'bookable-user-select'">
                                <label class="block text-sm font-medium text-gray-700 mb-1">{{ _mft(field.labelKey) }}</label>
                                <div v-if="loadingBookableUsers">
                                    <mf-spinner-medium class="w-24 h-12" />
                                </div>
                                <template v-else-if="bookableUsers.length > 0">
                                    <select
                                        v-model="configValues[field.key]"
                                        :required="Boolean(field.required)"
                                        class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                                    >
                                        <option :value="null" disabled>{{ _mft(field.labelKey) }}</option>
                                        <option v-for="user in bookableUsers" :key="user.uuid" :value="user.uuid">{{ user.name }}</option>
                                    </select>
                                </template>
                                <div v-else>
                                    <p class="italic py-2 text-sm text-gray-600">
                                        {{ _mft('pagebuilderBlock:cronofy.atLeastOneUserConfigured') }}
                                    </p>
                                    <a
                                        :href="route('manage-expert-profile.availability.show')"
                                        target="_blank"
                                    >
                                        <button-base>
                                            {{ _mft('pagebuilderBlock:cronofy.configureNow') }}
                                        </button-base>
                                    </a>
                                </div>
                            </template>
                            <template v-else-if="field.type === 'invoice-link-select'">
                                <label class="block text-sm font-medium text-gray-700 mb-1">{{ _mft(field.labelKey) }}</label>
                                <div v-if="loadingInvoiceLinks">
                                    <mf-spinner-medium class="w-24 h-12" />
                                </div>
                                <template v-else-if="invoiceLinks.length > 0">
                                    <select
                                        v-model="configValues[field.key]"
                                        :required="Boolean(field.required)"
                                        class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                                    >
                                        <option :value="null" disabled>{{ _mft(field.labelKey) }}</option>
                                        <option v-for="link in invoiceLinks" :key="link.uuid" :value="link.uuid">
                                            {{ formatInvoiceLinkOption(link) }}
                                        </option>
                                    </select>
                                </template>
                                <div v-else>
                                    <p class="italic py-2 text-sm text-gray-600">
                                        {{ _mft('page:aiPageBuilder.components.invoiceLink.empty') }}
                                    </p>
                                    <a
                                        :href="route('invoice-links.new')"
                                        target="_blank"
                                    >
                                        <button-base>
                                            {{ _mft('page:aiPageBuilder.components.invoiceLink.create') }}
                                        </button-base>
                                    </a>
                                </div>
                            </template>
                            <template v-else-if="field.type === 'color'">
                                <label class="block text-sm font-medium text-gray-700 mb-1">{{ _mft(field.labelKey) }}</label>
                                <div class="flex items-center gap-2">
                                    <input
                                        type="color"
                                        v-model="configValues[field.key]"
                                        class="h-9 w-12 rounded border border-gray-300 cursor-pointer"
                                    >
                                    <input
                                        type="text"
                                        v-model="configValues[field.key]"
                                        class="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm"
                                        placeholder="#000000"
                                    >
                                </div>
                            </template>
                            <template v-else-if="field.type === 'tag-select'">
                                <label class="block text-sm font-medium text-gray-700 mb-1">{{ _mft(field.labelKey) }}</label>
                                <tags-multi-select-v2
                                    :pre-selected-tags="getPreSelectedTag()"
                                    :select-multiple="false"
                                    :allow-creation="true"
                                    @tags-selected="handleTagSelected"
                                ></tags-multi-select-v2>
                            </template>
                            <template v-else-if="field.type === 'form-picker'">
                                <label class="block text-sm font-medium text-gray-700 mb-1">{{ _mft(field.labelKey) }}</label>
                                <input-published-form
                                    v-model="configValues[field.key]"
                                    :required="true"
                                />
                            </template>
                            <template v-else>
                                <label class="block text-sm font-medium text-gray-700 mb-1">{{ _mft(field.labelKey) }}</label>
                                <input
                                    type="text"
                                    v-model="configValues[field.key]"
                                    class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                                >
                            </template>
                        </div>
                    </template>
                </template>

                <div class="flex justify-end space-x-2 mt-6">
                    <button-danger
                        v-if="isEditingComponent"
                        @click="deleteComponent"
                        size="md"
                        class="mr-auto"
                    >
                        {{ _mft('shared:action.delete') }}
                    </button-danger>
                    <button-default
                        @click="closeConfigModal"
                        size="md"
                    >
                        {{ _mft('page:aiPageBuilder.components.cancel') }}
                    </button-default>
                    <button-primary
                        @click="confirmInsert"
                        :disabled="!canConfirm"
                        size="md"
                    >
                        {{ isEditingComponent || isEditingAttachment
                            ? _mft('page:aiPageBuilder.components.save')
                            : modalMode === 'attachment'
                                ? _mft('page:aiPageBuilder.attach.button')
                                : _mft('page:aiPageBuilder.components.insert') }}
                    </button-primary>
                </div>
            </template>
        </slideout-with-slots>
    </div>
</template>
